import type {ApiClient} from './client';
import type {SyncStreamEvent} from './types';

/**
 * `GET /v1/guest/stream` — the SSE stream, scoped to one table session.
 *
 * ## Why this is not `EventSource`
 *
 * `EventSource` cannot set an `Authorization` header, and the guest credential
 * is a bearer token. The alternatives are putting the token in the query string
 * — where it lands in every access log and proxy cache between the phone and
 * Bangkok — or reading the stream off `fetch`. This reads it off `fetch`.
 *
 * The cost is that reconnection is ours to write, which `EventSource` would have
 * given us. It is below, and it is deliberately conservative: exponential
 * backoff with jitter, capped, and it stops entirely when the caller aborts.
 *
 * ## What the stream is for, and what it is not
 *
 * It carries exactly two things — availability for the projected guest channel,
 * and the status of this table's own order. Every frame is a **hint to
 * re-read**, never truth. Nothing about money is confirmed by a pushed frame.
 *
 * ## Offline is normal
 *
 * Foundations §6: offline is the supported state of this product, not a fault.
 * A dropped stream is not an error to show a guest — it is a reconnect, and the
 * only thing a screen should surface is that live status may be stale (Q14 says
 * so explicitly: say it rather than freeze a progress bar).
 */

/**
 * Statuses that mean "stop asking", not "try again".
 *
 * 401/403 — the credential or the tenant's plan. 404 — no such route, which on
 * this surface means an API that does not serve the stream at all. Retrying any
 * of the three is a loop that cannot terminate, on a device whose battery is the
 * guest's own.
 */
const TERMINAL_REFUSALS: ReadonlySet<number> = new Set([401, 403, 404]);

export type StreamConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface GuestStreamHandlers {
  onEvent: (event: SyncStreamEvent) => void;
  /** Connection state changes. Q14 renders staleness from this, not from an error. */
  onStateChange?: (state: StreamConnectionState) => void;
  /**
   * `stream.reset` — the resume point is no longer in the replay buffer. The
   * client must re-read its snapshots (menu projection, order) from scratch.
   */
  onReset?: () => void;
}

export interface GuestStreamOptions extends GuestStreamHandlers {
  client: ApiClient;
  /** Resume point. The frame `id:` is `stream_seq`; echo the last one seen. */
  lastEventId?: string | null;
  signal: AbortSignal;
}

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

/**
 * Opens the stream and keeps it open until `signal` aborts. Resolves when the
 * caller aborts; it does not reject on a dropped connection, because a dropped
 * connection here is weather, not failure.
 */
export async function openGuestStream(options: GuestStreamOptions): Promise<void> {
  const {client, signal, onEvent, onStateChange, onReset} = options;
  let lastEventId = options.lastEventId ?? null;
  let attempt = 0;

  const setState = (state: StreamConnectionState) => onStateChange?.(state);

  setState('connecting');

  while (!signal.aborted) {
    try {
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        ...client.authHeaders(),
      };
      if (lastEventId) headers['Last-Event-ID'] = lastEventId;

      const response = await fetch(client.url('/v1/guest/stream'), {
        headers,
        signal,
        credentials: 'omit',
      });

      /*
       * A refusal the server will repeat forever is not weather.
       *
       * Everything else in this loop treats a failure as a dropped connection
       * and retries, which is right for a dining room. **401, 403 and 404 are
       * not that.** They are the server saying no about something that will not
       * change by asking again: an expired credential, a tenant whose plan does
       * not carry `guest_qr.order_pay`, a route that is not there.
       *
       * Found on the first real staging scan, 2026-08-14. `ฟ้าใส` is on the
       * `free` plan, the API answered the stream with
       * `403 entitlement_required`, and this loop reconnected against it every
       * few seconds — burning a guest's battery and radio on a request that had
       * already been answered, and pinning `กำลังเชื่อมต่อใหม่…` to the menu
       * with nothing behind it. The rest of the surface was working perfectly:
       * the menu had loaded, the order went through.
       *
       * So a terminal refusal closes the stream instead. The surface is built to
       * work with the stream doing nothing at all — `test/guest-path.test.tsx`
       * asserts exactly that against a body that never yields, because that is a
       * 4G dining room — and `closed` is a state Q14 already reads. What a guest
       * loses is live 86s and live kitchen progress, which is what a plan that
       * does not include this feature is meant to mean.
       */
      if (TERMINAL_REFUSALS.has(response.status)) {
        setState('closed');
        return;
      }

      if (!response.ok || !response.body) {
        throw new Error(`stream refused: ${response.status}`);
      }

      attempt = 0;
      setState('open');

      for await (const frame of readSseFrames(response.body, signal)) {
        if (frame.id) lastEventId = frame.id;
        if (frame.data === '') continue;

        const event = parseEvent(frame.data);
        if (!event) continue;

        if (event.type === 'stream.reset') {
          // The buffer moved past us. Everything cached is suspect; drop the
          // resume point so the next connection starts clean.
          lastEventId = null;
          onReset?.();
        }
        onEvent(event);
      }

      // A clean end of body is still a disconnect — reconnect.
      if (signal.aborted) break;
      setState('reconnecting');
    } catch {
      if (signal.aborted) break;
      setState('reconnecting');
    }

    attempt += 1;
    await sleep(backoffMs(attempt), signal);
  }

  setState('closed');
}

/** Exponential backoff with full jitter, capped. Deterministic when `random` is given. */
export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (attempt - 1));
  return Math.round(ceiling * (0.5 + random() * 0.5));
}

function parseEvent(data: string): SyncStreamEvent | null {
  try {
    const parsed = JSON.parse(data) as SyncStreamEvent;
    return typeof parsed?.type === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export interface SseFrame {
  id: string | null;
  event: string | null;
  data: string;
}

/**
 * Minimal SSE frame parser over a byte stream.
 *
 * Handles the parts of the wire format this stream actually uses: `id:`,
 * `event:`, multi-line `data:`, comment lines (`:` heartbeats), and CRLF or LF
 * line endings. It does not implement `retry:` — the backoff above is ours.
 */
export async function* readSseFrames(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (!signal?.aborted) {
      const {done, value} = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, {stream: true});
      buffer = buffer.replace(/\r\n/g, '\n');

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const frame = parseFrame(block);
        if (frame) yield frame;
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}

function parseFrame(block: string): SseFrame | null {
  let id: string | null = null;
  let event: string | null = null;
  const data: string[] = [];

  for (const line of block.split('\n')) {
    if (line === '' || line.startsWith(':')) continue; // heartbeat comment
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'id') id = value;
    else if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }

  if (id === null && event === null && data.length === 0) return null;
  return {id, event, data: data.join('\n')};
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done, {once: true});
    function done() {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
  });
}
